import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import PeriodFilter from '@/components/stats/period-filter';
import type { StatsPeriod } from '@/entities/admin-stats.entity';
import { PageScroll } from '@/layouts/page-layout';
import LandingsGate from '@/pages/landings/landings-gate';
import { cn } from '@/lib/utils';
import { BarChart3Icon, ListIcon } from 'lucide-react';
import { useState } from 'react';
import LandingEventsLogTab from './landing-events-log-tab';
import LandingEventsStatsTab from './landing-events-stats-tab';
import LandingLinkSelect from './landing-link-select';

type EventsTab = 'stats' | 'log';

const TABS: { id: EventsTab; label: string; icon: typeof BarChart3Icon }[] = [
	{ id: 'stats', label: 'Статистика', icon: BarChart3Icon },
	{ id: 'log', label: 'Лог', icon: ListIcon },
];

export default function LandingsEventsPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<EventsContent />
			</LandingsGate>
		</PageScroll>
	);
}

function EventsContent() {
	const [tab, setTab] = useState<EventsTab>('stats');
	const [period, setPeriod] = useState<StatsPeriod>('week');
	const [linkId, setLinkId] = useState('');

	function handlePeriodChange(next: StatsPeriod) {
		if (next === period) return;
		setPeriod(next);
	}

	return (
		<div className='flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-chart-1/15 p-2'>
							<BarChart3Icon className='text-chart-1' size={20} />
						</div>
						<div>
							<CardTitle className='text-chart-1'>События лендингов</CardTitle>
							<CardDescription>Статистика и лог connect / download / form / TG auth</CardDescription>
						</div>
					</div>
				</CardHeader>
			</Card>

			<div className='flex gap-1.5' role='tablist' aria-label='Раздел событий'>
				{TABS.map(item => {
					const Icon = item.icon;
					const active = tab === item.id;
					return (
						<button
							key={item.id}
							type='button'
							role='tab'
							aria-selected={active}
							id={`landings-events-tab-${item.id}`}
							aria-controls={`landings-events-panel-${item.id}`}
							onClick={() => setTab(item.id)}
							className={cn(
								'flex-1 h-9 px-3 rounded-lg border text-sm inline-flex items-center justify-center gap-1.5 transition-colors',
								active
									? 'bg-secondary border-border text-foreground font-semibold shadow-sm'
									: 'border-border/60 bg-card/40 text-muted-foreground hover:bg-accent',
							)}
						>
							<Icon size={16} className={cn(active && 'text-chart-1')} />
							{item.label}
						</button>
					);
				})}
			</div>

			<PeriodFilter period={period} onChange={handlePeriodChange} />
			<LandingLinkSelect value={linkId} onChange={setLinkId} />

			{tab === 'stats' ? (
				<div role='tabpanel' id='landings-events-panel-stats' aria-labelledby='landings-events-tab-stats'>
					<LandingEventsStatsTab period={period} linkId={linkId} />
				</div>
			) : (
				<div role='tabpanel' id='landings-events-panel-log' aria-labelledby='landings-events-tab-log'>
					<LandingEventsLogTab period={period} linkId={linkId} />
				</div>
			)}
		</div>
	);
}
